import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchAllListRoles } from '../../../../redux/slices/RolesManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomCheckbox from '../../../../shared-components/custom-check-box/CustomCheckBox';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomInputSwitch from '../../../../shared-components/custom-input-switch/CustomInputSwitch';
import SelectField from '../../../../shared-components/select-field/SelectField';

interface SaveDashboardModalProperties {
  onClose: () => void;
  onSave: (data: SaveData) => void;
  loading?: boolean;
  setLoading: (loading: boolean) => void;
  initialData?: SaveData;
}

interface SaveData {
  name: string;
  showTo: string;
  showAsTab: boolean;
  isDefault: boolean;
}

/**
 * SaveDashboardModal component
 * Displays a popup to save the customized dashboard layout
 * @param {any} props - Component properties
 * @param {any} props.onClose - The onClose prop
 * @param {any} props.onSave - The onSave prop
 * @param {any} props.loading - The loading prop
 * @param {any} props.setLoading - The setLoading prop
 * @param {any} props.initialData - The initialData prop
 * @returns {any} JSX Element
 */
function SaveDashboardModal({
  onClose,
  onSave,
  loading = false,
  setLoading,
  initialData,
}: SaveDashboardModalProperties) {
  const dispatch = useAppDispatch();
  const { rolesList } = useAppSelector((state) => state.roles);
  const [formData, setFormData] = useState<SaveData>({
    name: '',
    showTo: '',
    showAsTab: false,
    isDefault: false,
  });
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  /**
   * Handles form field changes
   * @param {any} name - Field name
   * @param {any} value - Field value
   */
  const handleChange = (name: string, value: any) => {
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  /**
   * Handles save action
   */
  const handleSave = () => {
    setLoading(true);
    onSave(formData);
  };
  useEffect(() => {
    if (formData?.name && formData?.showTo) {
      setIsSaveButtonDisabled(false);
    } else {
      setIsSaveButtonDisabled(true);
    }
  }, [formData]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    dispatch(fetchAllListRoles());
  }, []);
  const modalContent = (
    <div className="save-dashboard-modal-content">
      <div className="field-group">
        <CustomInput
          inputLabel="Dashboard Name"
          type="text"
          placeholder="Enter dashboard name"
          name="name"
          value={formData.name}
          onChangeValue={handleChange}
          fontSize="medium"
          valueRequired
        />
      </div>

      <div className="field-group">
        <SelectField
          inputLabel="Show This Dashboard To"
          placeholder="Choose user"
          name="showTo"
          options={rolesList}
          selectedValue={formData.showTo}
          onChangeValue={handleChange}
          fontSize="medium"
          valueRequired
        />
      </div>

      <div className="field-group checkbox-group">
        <CustomCheckbox
          checkboxLabel="Show as a Tab in Main Dashboard"
          name="showAsTab"
          value={formData.showAsTab}
          onChangeValue={handleChange}
          fontSize="medium"
        />
      </div>

      <div className="field-group switch-group">
        <CustomInputSwitch
          inputLabel="Set as default"
          name="isDefault"
          value={formData.isDefault}
          onChangeValue={handleChange}
          fontSize="medium"
        />
      </div>
      <div className="modal-actions">
        <MyButton
          label="Cancel"
          buttonType="outline"
          onClickFunc={onClose}
          disableButton={loading}
        />
        <MyButton
          label="Save"
          buttonType="submit"
          onClickFunc={handleSave}
          loading={loading}
          disableButton={isSaveButtonDisabled}
        />
      </div>
    </div>
  );

  return modalContent;
}
SaveDashboardModal.defaultProps = {
  loading: false,
  initialData: null,
};
export default SaveDashboardModal;
